import { IEntityInfo, NSARouterMakerResult } from "namirasoft-account-react";
import { ProductUUID } from "namirasoft-api-product";
import { IStorageLocal } from "namirasoft-core";
import { EntryInputRow, EntryRow, TemplateInputRow, TemplateRow, UserFullRow } from "namirasoft-password";
import { NamirasoftMap } from "namirasoft-site-map";
import { BaseColumnFormatter, BooleanFormatter, DateTimeFormatter, EnumFormatter, IDFormatter, IntegerFormatter, StringFormatter, StringFormatterSizeType, URLFormatter } from "namirasoft-site-react";
import { PasswordMenus } from "./PasswordMenus";
import { PasswordRouterProps } from "./PasswordRouterProps";
import { PasswordRoutes } from "./PasswordRoutes";
import { EntryLineFormatter } from "./formatters/EntryLineFormatter";

export class PasswordEntities
{
    static entry(props: NSARouterMakerResult & PasswordRouterProps & { user: UserFullRow }): IEntityInfo<EntryRow, EntryInputRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_PASSWORD,
            name: "entry",
            tables: [{ table: namirasoft.password.tables.entry, required: false }],
            client: {
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.name, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.domain, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.entry_line, new EntryLineFormatter(props)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.host, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.url, new URLFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.entry.columns.updated_at, new DateTimeFormatter()),
                    ]
                },
                getListURL: () => PasswordRoutes.EntryList,
                getNewURL: () => PasswordRoutes.EntryNew,
                getViewURL: (id) => PasswordRoutes.EntryView(id),
                getEditURL: (id) => PasswordRoutes.EntryEdit(id),
                getCopyURL: (id) => PasswordRoutes.EntryCopy(id),
            },
            server: {
                value_list: props.servers.password.value.List,
                list: props.servers.password.entry.List,
                get: props.servers.password.entry.Get,
                create: props.servers.password.entry.Create,
                update: props.servers.password.entry.Update,
                delete: props.servers.password.entry.Delete,
            }
        }

    }
    static template(props: NSARouterMakerResult & PasswordRouterProps): IEntityInfo<TemplateRow, TemplateInputRow>
    {
        let namirasoft = new NamirasoftMap(props.account.token_manager, console.error);

        return {
            product_id: ProductUUID.NAMIRASOFT_PASSWORD,
            name: "template",
            tables: [{ table: namirasoft.password.tables.template, required: false }],
            client: {
                getListURL: () => PasswordRoutes.TemplateList,
                getNewURL: () => PasswordRoutes.TemplateNew,
                getViewURL: (id) => PasswordRoutes.TemplateView(id),
                getEditURL: (id) => PasswordRoutes.TemplateEdit(id),
                getCopyURL: (id) => PasswordRoutes.TemplateCopy(id),
                getColumns: () =>
                {
                    return [
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.id, new IDFormatter(namirasoft, null)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.user_id, new IDFormatter(namirasoft, new IStorageLocal())),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.name, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.length, new IntegerFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.type, new EnumFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_uppercase, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_lowercase, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_digit, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_minus, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_underline, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_space, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_special, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_brackets, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.character_set_latin_1_supplement, new BooleanFormatter("Yes", "No", StringFormatterSizeType.FullName)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.pattern, new StringFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.pattern_randomly_permute, new BooleanFormatter("Yes", "No", StringFormatterSizeType.Sentence)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.description, new StringFormatter(StringFormatterSizeType.Description)),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.created_at, new DateTimeFormatter()),
                        BaseColumnFormatter.getColumn(namirasoft.password.tables.template.columns.updated_at, new DateTimeFormatter()),
                    ]
                },
                getMenus: (_, getRows) => ({ "Template": [PasswordMenus.generate(props, getRows)] })

            },
            server: {
                value_list: props.servers.password.value.List,
                list: props.servers.password.template.List,
                get: props.servers.password.template.Get,
                create: props.servers.password.template.Create,
                update: props.servers.password.template.Update,
                delete: props.servers.password.template.Delete,
            }
        }
    }
}